import { FunctionComponent, useState, useEffect } from 'react'
import useAuth from '../stores/auth'
import MetricCard from '../components/Cards/MetricCard'
import RecentMembersCard from '../components/Cards/RecentMembersCard'
import TopBenefitsCard from '../components/Cards/TopBenefitsCard'
import UpcomingRenewalsCard from '../components/Cards/UpcomingRenewalsCard'
import RevenueChartCard from '../components/Cards/RevenueChartCard'
import SetupRecommendationsCard from '../components/Cards/SetupRecommendationsCard'
import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import colors from '../colors'
import axios from '../utils/axios'
import { RecentMember, TopBenefit, MostPopularMembership, UpcomingRenewal, AtRiskPatron, MonthlyRevenue } from '../types'
import { ChevronDown, ChevronUp } from 'react-feather'

const useStyles = makeStyles({
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    height: '100%',
    minWidth: 0,
    '@media (min-width: 1280px)': {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
    '& > *': {
      minWidth: 0,
    },
  },
  // Two thirds to the chart, one third to recent members. Stacks below
  // 1024px, where side by side would squeeze both.
  chartRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 16,
    marginTop: 16,
    minWidth: 0,
    '& > *': { minWidth: 0 },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '2fr 1fr',
      alignItems: 'stretch',
    },
  },
  splitRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 16,
    marginTop: 16,
    minWidth: 0,
    '& > *': { minWidth: 0 },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      alignItems: 'stretch',
    },
  },
  moreSection: {
    marginTop: 24,
  },
  moreToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: colors.GREY_2,
    '&:hover': { color: colors.BRIGHT_BLUE },
  },
})

const Dashboard: FunctionComponent = () => {
  const styles = useStyles();
  const { logout, user } = useAuth();
  const [showMore, setShowMore] = useState(false)
  // The brewery's public storefront, for the empty state that most needs a
  // way forward. A login-time snapshot, but the slug rarely changes.
  const signupLink = user?.organization?.vipLink
  const isSuperAdmin = user?.role === 'super_admin';

  // Legacy metrics (original 3)
  const [fetchingStats, setFetchingStats] = useState(true)
  const [mrr, setMrr] = useState('')
  const [activePatrons, setActivePatrons] = useState('')
  const [benefitUsage, setBenefitUsage] = useState('')
  // Guards every rate on the page: a percentage computed over nobody is
  // arithmetically fine and tells the brewery something untrue.
  const hasMembers = Number(activePatrons) > 0

  // New metrics with individual loading states
  const [loadingRecentMembers, setLoadingRecentMembers] = useState(true)
  const [loadingTopBenefits, setLoadingTopBenefits] = useState(true)
  const [loadingNewPatrons, setLoadingNewPatrons] = useState(true)
  const [loadingChurnRate, setLoadingChurnRate] = useState(true)
  const [loadingRetentionRate, setLoadingRetentionRate] = useState(true)
  const [loadingMostPopular, setLoadingMostPopular] = useState(true)
  const [loadingUpcomingRenewals, setLoadingUpcomingRenewals] = useState(true)
  const [loadingAtRiskPatrons, setLoadingAtRiskPatrons] = useState(true)
  const [atRiskPatrons, setAtRiskPatrons] = useState<AtRiskPatron[]>([])
  const [atRiskPatronCount, setAtRiskPatronCount] = useState(0)
  const [loadingMonthlyRevenue, setLoadingMonthlyRevenue] = useState(true)
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([])

  const [recentMembers, setRecentMembers] = useState<RecentMember[]>([])
  const [topBenefits, setTopBenefits] = useState<TopBenefit[]>([])
  const [newPatronsThisMonth, setNewPatronsThisMonth] = useState(0)
  const [churnRate, setChurnRate] = useState(0)
  const [retentionRate, setRetentionRate] = useState(0)
  const [mostPopularMembership, setMostPopularMembership] = useState<MostPopularMembership | null>(null)
  const [upcomingRenewals, setUpcomingRenewals] = useState<UpcomingRenewal[]>([])

  // Fetch legacy metrics (MRR, Total Patrons, Benefit Usage)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/dashboard_stats')
        setMrr(res.data.mrr)
        setActivePatrons(res.data.activeMemberships)
        setBenefitUsage(res.data.benefitUsage)
        setFetchingStats(false)
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
        setFetchingStats(false)
      }
    })()
  }, [logout])

  // Fetch recent members
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/recent_members')
        setRecentMembers(res.data.recentMembers || [])
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingRecentMembers(false)
    })()
  }, [logout])

  // Fetch top redeemed benefits
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/top_redeemed_benefits')
        setTopBenefits(res.data.topRedeemedBenefits || [])
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingTopBenefits(false)
    })()
  }, [logout])

  // Fetch new patrons this month
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/new_patrons_this_month')
        setNewPatronsThisMonth(res.data.newPatronsThisMonth || 0)
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingNewPatrons(false)
    })()
  }, [logout])

  // Fetch churn rate
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/churn_rate')
        setChurnRate(res.data.churnRate || 0)
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingChurnRate(false)
    })()
  }, [logout])

  // Fetch retention rate
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/retention_rate')
        setRetentionRate(res.data.retentionRate || 0)
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingRetentionRate(false)
    })()
  }, [logout])

  // Fetch most popular membership
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/most_popular_membership')
        setMostPopularMembership(res.data.mostPopularMembership || null)
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingMostPopular(false)
    })()
  }, [logout])

  // Fetch upcoming renewals
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/upcoming_renewals')
        setUpcomingRenewals(res.data.upcomingRenewals || [])
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingUpcomingRenewals(false)
    })()
  }, [logout])

  // Fetch at-risk patrons
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/at_risk_patrons')
        setAtRiskPatronCount(res.data.at_risk_patrons || 0)
        const patrons = (res.data.at_risk_patrons_details || []).map((p: any) => ({
          id: p.id,
          patronName: p.patron_name,
          patronEmail: p.patron_email,
          membershipTitle: p.membership_title,
          lastRedemptionDate: p.last_redemption_at,
          joinedDate: p.joined_at,
        })) as AtRiskPatron[]
        setAtRiskPatrons(patrons)
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingAtRiskPatrons(false)
    })()
  }, [logout])

  // Fetch monthly revenue
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/monthly_revenue')
        setMonthlyRevenue(res.data.monthlyRevenue || [])
      } catch (e) {
        if ((e as Error).message.includes("401")) {
          logout();
        }
      }
      setLoadingMonthlyRevenue(false)
    })()
  }, [logout])

  return (
    <Box>

      {/* Onboarding checklist, and the feedback prompt it gates. Primary
          action on the dashboard: it is what leads to payments. */}
      <SetupRecommendationsCard />

      {/* Four primary KPIs, one per card. Churn, benefit usage and the
          most popular membership are secondary and live below, behind a
          toggle - for a brewery with no members yet, analytics are noise. */}
      <Box className={styles.kpiRow}>
        <MetricCard
          loading={fetchingStats}
          title="Active Members"
          value={activePatrons}
          info={"The number of patrons actively subscribed to any of your memberships"}
        />
        <MetricCard
          loading={fetchingStats}
          title="MRR"
          value={mrr}
          info={"Monthly Recurring Revenue: Given your current membership enrollment, this is the average membership revenue you can expect to net each month."}
        />
        <MetricCard
          loading={loadingNewPatrons}
          title="New Signups"
          value={String(newPatronsThisMonth)}
          info={"Patrons who joined one of your memberships this month."}
        />
        <MetricCard
          loading={loadingRetentionRate}
          title="Retention Rate"
          // A dash until there is somebody to retain: "100%" with no members
          // is arithmetically true and completely misleading.
          value={hasMembers ? `${retentionRate.toFixed(1)}%` : '\u2014'}
          info={"The percentage of patrons who remained subscribed this month. Shown once you have members."}
        />
      </Box>

      {/* Revenue gets two thirds, recent members the rest - a line chart does
          not need the full width, and the two read well side by side. */}
      <Box className={styles.chartRow}>
        <RevenueChartCard
          data={monthlyRevenue}
          loading={loadingMonthlyRevenue}
        />
        <RecentMembersCard
          members={recentMembers}
          loading={loadingRecentMembers}
          emptyAction={signupLink
            ? { label: 'Share your signup link', href: signupLink }
            : { label: 'Set up a membership', to: '/memberships' }}
        />
      </Box>

      <Box className={styles.splitRow}>
        <UpcomingRenewalsCard
          renewals={upcomingRenewals}
          loading={loadingUpcomingRenewals}
        />
        <TopBenefitsCard
          benefits={topBenefits}
          loading={loadingTopBenefits}
          emptyAction={{ label: 'Manage benefits', to: '/memberships' }}
        />
      </Box>

      <Box className={styles.moreSection}>
        <button
          type="button"
          className={styles.moreToggle}
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          <span style={{ marginLeft: 6 }}>More analytics</span>
        </button>

        {showMore && (
          <Box className={styles.kpiRow} style={{ marginTop: 12 }}>
            <MetricCard
              loading={loadingChurnRate}
              title="Churn Rate"
              value={hasMembers ? `${churnRate.toFixed(1)}%` : '\u2014'}
              info={"The percentage of patrons who cancelled their subscriptions this month."}
            />
            <MetricCard
              loading={fetchingStats}
              title="Benefit Usage"
              value={hasMembers ? (benefitUsage || '\u2014') : '\u2014'}
              info={"The percentage of benefits issued in the last 30 days that have been redeemed."}
            />
            <MetricCard
              loading={loadingMostPopular}
              title="Most Popular"
              value={mostPopularMembership?.title || '\u2014'}
              info={"The membership with the most active subscribers."}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default Dashboard
